import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { toPng } from "html-to-image";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import Dashboard from "../../Components/Dashboard";
import Denied from "../../Components/Denied";
import { apiIDW, apiLabs } from "../../Services/api";
import history from "../../Services/history";
import { store } from "../../store";

import {
  globalCSS,
  page,
  dash,
  header,
  uploads,
  actions,
  kpis,
  breakdown,
  issues,
  footer,
  charts,
  baseline as baselineStyle,
  colors,
  balanceState,
} from "./style";

// ─────────────────────────────────────────────────────────────────────────────
// Configuração de transportadoras
// ─────────────────────────────────────────────────────────────────────────────
const BATCH_SIZE = 40;
const BATCH_DELAY_MS = 1000;

// Base de pedidos do mês (apiIDW /orders paginado)
const PAGE_SIZE = 2000; // máximo de registros por página da apiIDW
const PAGE_DELAY_MS = 500; // espaçamento entre páginas (evita rate limit)
const BASELINE_ORDER_TYPES = "877,875,871";

const CARRIERS = [
  {
    id: "TM",
    label: "TM",
    lookup: "nf",
    headerRow: 7,
    nfCols: ["Nota Fiscal"],
    valueCols: ["R$ Sub-Total"],
  },
  {
    id: "MANDAE",
    label: "MANDAE",
    lookup: "nf",
    headerRow: 0,
    nfCols: ["nro_nf"],
    valueCols: ["preco_frete"],
  },
  {
    id: "R3",
    label: "R3",
    lookup: "nf",
    headerRow: 0,
    nfCols: ["Notas fiscais", "Notas Fiscais"],
    valueCols: ["Valor total do frete"],
    stripSeries: true, // "4-656354" → "656354"
  },
  {
    id: "CORREIOS",
    label: "CORREIOS",
    lookup: "nf",
    headerRow: 0,
    nfCols: ["NF"],
    valueCols: ["VALOR"],
  },
  {
    id: "ALEF",
    label: "ALEF",
    lookup: "nf",
    headerRow: 0,
    nfCols: ["NOTA FISCAL"],
    valueCols: [" Valor (R$) ", "Valor (R$)", "Valor"],
  },
  {
    id: "LALAMOVE",
    label: "LALAMOVE",
    lookup: "order",
    headerRow: 0,
    // Lê apenas a primeira aba — usuário deve subir um arquivo por período
    orderCols: ["Pedido sonar"],
    valueCols: [" Valor corrida ", "Valor corrida"],
  },
];

const CARRIER_BY_ID = Object.fromEntries(CARRIERS.map((c) => [c.id, c]));

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const fmtNumber = (n) => Number(n || 0).toLocaleString("pt-BR");

const fmtPct = (n, digits = 1) =>
  n == null || isNaN(n) ? "—" : `${Number(n).toFixed(digits).replace(".", ",")}%`;

const fmtKg = (n) => {
  if (n == null || isNaN(n) || n === 0) return "—";
  return `${Number(n).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} kg`;
};

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

const pad2 = (n) => String(n).padStart(2, "0");

const fmtDateYMD = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const fmtDateBR = (ymd) => {
  if (!ymd) return "—";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
};

// Período da base: início do mês corrente → D-1 (ontem)
function getBaselineRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now);
  to.setDate(to.getDate() - 1); // D-1
  // se hoje for dia 1, D-1 cai no mês anterior → mantém o início do mês
  if (to < from) return { dateFrom: fmtDateYMD(from), dateTo: fmtDateYMD(from) };
  return { dateFrom: fmtDateYMD(from), dateTo: fmtDateYMD(to) };
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// Aceita string "R$ 25,39", "25,39", "25.39", número, etc.
function parseCurrency(raw) {
  if (raw == null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/R\$\s*/i, "").replace(/\s+/g, "").replace(/ /g, "");
  // remove milhar e troca decimal vírgula por ponto
  if (s.includes(",") && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(",")) {
    s = s.replace(",", ".");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function normalizeNF(raw, stripSeries) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  if (stripSeries && s.includes("-")) {
    const parts = s.split("-");
    s = parts[parts.length - 1].trim();
  }
  s = s.replace(/\D+/g, "");
  return s || null;
}

function normalizeOrder(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s || s === "-") return null;
  return s;
}

function firstKey(row, candidates) {
  for (const c of candidates) if (c in row) return c;
  // tolera variações com/sem espaço
  const keys = Object.keys(row);
  for (const c of candidates) {
    const norm = c.trim().toLowerCase();
    const found = keys.find((k) => k.trim().toLowerCase() === norm);
    if (found) return found;
  }
  return null;
}

function parseSheet(workbook, carrier) {
  const items = [];
  // Sempre lê apenas a primeira aba — múltiplos períodos devem ser uploads separados
  const sheetNames = [workbook.SheetNames[0]];

  for (const name of sheetNames) {
    const ws = workbook.Sheets[name];
    if (!ws) continue;
    const rows = XLSX.utils.sheet_to_json(ws, {
      defval: null,
      range: carrier.headerRow || 0,
    });

    for (const row of rows) {
      const keyCol = firstKey(
        row,
        carrier.lookup === "nf" ? carrier.nfCols : carrier.orderCols
      );
      const valueCol = firstKey(row, carrier.valueCols);
      if (!keyCol || !valueCol) continue;

      let key;
      if (carrier.lookup === "nf") {
        key = normalizeNF(row[keyCol], carrier.stripSeries);
      } else {
        key = normalizeOrder(row[keyCol]);
      }
      const value = parseCurrency(row[valueCol]);
      if (!key || value == null) continue;

      items.push({ key, value });
    }
  }

  // Agrega valores com mesma chave (ex.: R3 cobra por volume → soma)
  const map = new Map();
  for (const it of items) {
    const prev = map.get(it.key);
    if (prev) {
      prev.value += it.value;
      prev.count += 1;
    } else {
      map.set(it.key, { key: it.key, value: it.value, count: 1 });
    }
  }

  const aggregated = Array.from(map.values());
  return {
    items: aggregated,
    totalRows: items.length,
    totalKeys: aggregated.length,
    totalValue: aggregated.reduce((s, x) => s + x.value, 0),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Exportações
// ─────────────────────────────────────────────────────────────────────────────
function timestampSlug() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

async function exportNodeToPng(node, fileName) {
  if (!node) throw new Error("Elemento não encontrado");
  const dataUrl = await toPng(node, {
    backgroundColor: "#F5F2EA",
    pixelRatio: 2,
    cacheBust: true,
    style: { boxShadow: "none" },
  });
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  link.click();
}

function exportCarrierRowsToXlsx(rows, grandTotals, fileName) {
  const data = rows.map((r) => {
    const saldo = r.totalShippingCustomer - r.totalPaidCarrier;
    const pricePerKg = r.totalKg > 0 ? r.totalPaidCarrier / r.totalKg : null;
    const pctRecuperacao =
      r.totalPaidCarrier > 0
        ? (r.totalShippingCustomer / r.totalPaidCarrier) * 100
        : null;
    return {
      Transportadora: r.label,
      Pedidos: r.matched,
      "Pago à transp. (R$)": Number(r.totalPaidCarrier.toFixed(2)),
      "Frete cliente (R$)": Number(r.totalShippingCustomer.toFixed(2)),
      "Saldo (R$)": Number(saldo.toFixed(2)),
      "% frete cliente": pctRecuperacao != null ? Number(pctRecuperacao.toFixed(2)) : null,
      "Peso total (kg)": Number(r.totalKg.toFixed(3)),
      "R$ / kg": pricePerKg != null ? Number(pricePerKg.toFixed(4)) : null,
      "Não encontradas": r.missing,
    };
  });

  const totalRow = {
    Transportadora: "TOTAL",
    Pedidos: grandTotals.matched,
    "Pago à transp. (R$)": Number(grandTotals.totalPaidCarrier.toFixed(2)),
    "Frete cliente (R$)": Number(grandTotals.totalShippingCustomer.toFixed(2)),
    "Saldo (R$)": Number(grandTotals.saldo.toFixed(2)),
    "% frete cliente":
      grandTotals.totalPaidCarrier > 0
        ? Number(
            (
              (grandTotals.totalShippingCustomer / grandTotals.totalPaidCarrier) *
              100
            ).toFixed(2)
          )
        : null,
    "Peso total (kg)": Number((grandTotals.totalKg || 0).toFixed(3)),
    "R$ / kg":
      grandTotals.totalKg > 0
        ? Number((grandTotals.totalPaidCarrier / grandTotals.totalKg).toFixed(4))
        : null,
    "Não encontradas": grandTotals.missing,
  };

  const ws = XLSX.utils.json_to_sheet([...data, totalRow]);
  ws["!cols"] = [
    { wch: 18 },
    { wch: 10 },
    { wch: 18 },
    { wch: 18 },
    { wch: 14 },
    { wch: 16 },
    { wch: 16 },
    { wch: 12 },
    { wch: 16 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Por Transportadora");
  XLSX.writeFile(wb, fileName);
}

// ─────────────────────────────────────────────────────────────────────────────
// Ícones
// ─────────────────────────────────────────────────────────────────────────────
function UploadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="9" r="1.6" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

function SheetIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="12" y1="13" x2="12" y2="21" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Componentes
// ─────────────────────────────────────────────────────────────────────────────
function UploadCard({ idx, carrier, dataset, onPick, onClear }) {
  const inputRef = useRef(null);

  function handlePick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    onPick(carrier.id, file);
    // reset para permitir reupload do mesmo nome
    e.target.value = "";
  }

  const hasData = Boolean(dataset);

  return (
    <div style={uploads.card(idx, hasData)}>
      <div style={uploads.cardTop}>
        <h4 style={uploads.carrier(idx)}>{carrier.label}</h4>
        <span style={uploads.badge(hasData ? "ready" : "empty")}>
          {hasData ? "PRONTO" : "VAZIO"}
        </span>
      </div>

      <div style={uploads.meta}>
        {hasData ? (
          <>
            <div>
              <span style={uploads.metaStrong}>{fmtNumber(dataset.totalKeys)}</span>{" "}
              {carrier.lookup === "nf" ? "notas únicas" : "pedidos únicos"}
              {dataset.totalRows !== dataset.totalKeys && (
                <>
                  {" "}
                  · <span style={uploads.metaStrong}>{fmtNumber(dataset.totalRows)}</span> linhas
                </>
              )}
            </div>
            <div>
              Valor da planilha:{" "}
              <span style={uploads.metaStrong}>{fmtMoney(dataset.totalValue)}</span>
            </div>
            <div style={uploads.hint}>{dataset.fileName}</div>
          </>
        ) : (
          <>
            <div>
              Busca por <strong>{carrier.lookup === "nf" ? "Nota Fiscal" : "Pedido"}</strong>
            </div>
            <div style={uploads.hint}>Selecione a planilha .xls / .xlsx</div>
          </>
        )}
      </div>

      <div style={uploads.actions}>
        <input
          ref={inputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          onChange={handlePick}
          style={{ display: "none" }}
        />
        <button
          type="button"
          style={uploads.fileBtn}
          onClick={() => inputRef.current?.click()}
        >
          <UploadIcon />
          {hasData ? "SUBSTITUIR" : "SELECIONAR"}
        </button>
        {hasData && (
          <button
            type="button"
            style={uploads.fileBtnGhost}
            onClick={() => onClear(carrier.id)}
            title="Limpar upload"
          >
            <TrashIcon />
            LIMPAR
          </button>
        )}
      </div>
    </div>
  );
}

function ActionsBar({
  totals,
  busy,
  progress,
  onProcess,
  hasResult,
  onClearResult,
  onExportPng,
  exporting,
}) {
  const pct =
    progress && progress.total > 0
      ? Math.round((progress.current / progress.total) * 100)
      : 0;

  const disabled = busy || totals.totalKeys === 0;

  return (
    <section style={actions.wrap}>
      <div style={actions.left}>
        <div style={actions.label}>NOTAS / PEDIDOS PRONTOS PARA CONSULTA</div>
        <div style={actions.value}>
          {fmtNumber(totals.totalKeys)}
          {totals.totalKeys > 0 && (
            <span style={{ color: colors.textMuted, marginLeft: 10, fontFamily: "inherit", fontSize: 13, fontWeight: 500 }}>
              · {fmtNumber(totals.totalNFs)} NFs · {fmtNumber(totals.totalOrders)} pedidos
            </span>
          )}
        </div>
      </div>

      <div style={actions.right}>
        {busy && (
          <div style={actions.progress}>
            <div style={actions.progressLabel}>
              CONSULTANDO API · {pct}% ({fmtNumber(progress.current)}/{fmtNumber(progress.total)})
            </div>
            <div style={actions.progressBar}>
              <div style={actions.progressFill(pct)} />
            </div>
          </div>
        )}

        {hasResult && !busy && (
          <>
            <button
              type="button"
              style={actions.exportBtn}
              onClick={onExportPng}
              disabled={exporting}
              title="Exportar todo o resultado abaixo em PNG"
            >
              <ImageIcon />
              {exporting ? "GERANDO..." : "EXPORTAR PNG"}
            </button>
            <button
              type="button"
              style={{ ...header.clearBtn }}
              onClick={onClearResult}
            >
              <TrashIcon />
              LIMPAR RESULTADO
            </button>
          </>
        )}

        <button
          type="button"
          style={actions.process(disabled)}
          onClick={onProcess}
          disabled={disabled}
        >
          <PlayIcon />
          PROCESSAR
        </button>
      </div>
    </section>
  );
}

function BaselinePanel({ baseline, onReload }) {
  const { loading, done, error, dateFrom, dateTo, page, count, totalShipping } = baseline;

  return (
    <section style={baselineStyle.wrap(done)}>
      <div style={baselineStyle.head}>
        <div style={baselineStyle.headLeft}>
          <span style={baselineStyle.tag(done, loading)}>
            {loading ? "CARREGANDO" : done ? "BASE PRONTA" : error ? "FALHA" : "AGUARDANDO"}
          </span>
          <div>
            <h2 style={baselineStyle.title}>BASE DE PEDIDOS DO MÊS</h2>
            <p style={baselineStyle.sub}>
              Todos os pedidos criados de {fmtDateBR(dateFrom)} até {fmtDateBR(dateTo)} (D-1) ·
              tipos {BASELINE_ORDER_TYPES} · apiIDW paginada
            </p>
          </div>
        </div>
        {!loading && (
          <button
            type="button"
            style={baselineStyle.reloadBtn}
            onClick={onReload}
            title="Recarregar base do mês"
          >
            {done ? "ATUALIZAR BASE" : "CARREGAR BASE"}
          </button>
        )}
      </div>

      {error && <div style={baselineStyle.error}>⚠ {error}</div>}

      {loading && (
        <div style={baselineStyle.loadingRow}>
          <div style={baselineStyle.spinner} />
          <span>
            Consultando página {fmtNumber(page || 0)} · {fmtNumber(count)} pedidos carregados...
          </span>
        </div>
      )}

      {done && (
        <div style={baselineStyle.metrics}>
          <div style={baselineStyle.metric}>
            <span style={baselineStyle.metricLabel}>PEDIDOS NO PERÍODO</span>
            <span style={baselineStyle.metricValue}>{fmtNumber(count)}</span>
          </div>
          <div style={baselineStyle.metric}>
            <span style={baselineStyle.metricLabel}>TOTAL FRETE PAGO PELO CLIENTE</span>
            <span style={baselineStyle.metricValue}>{fmtMoney(totalShipping)}</span>
          </div>
          <div style={baselineStyle.metric}>
            <span style={baselineStyle.metricLabel}>PÁGINAS CONSULTADAS</span>
            <span style={baselineStyle.metricValue}>{fmtNumber(page)}</span>
          </div>
          <div style={baselineStyle.metric}>
            <span style={baselineStyle.metricLabel}>FRETE MÉDIO / PEDIDO</span>
            <span style={baselineStyle.metricValue}>
              {count > 0 ? fmtMoney(totalShipping / count) : "—"}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

function Kpi({ idx, label, value, sub, pill }) {
  return (
    <div style={kpis.card(idx)}>
      <div style={kpis.label}>{label}</div>
      <div style={kpis.value(pill?.state || "neutral")}>{value}</div>
      {sub && <div style={kpis.sub}>{sub}</div>}
      {pill && <div style={kpis.pill(pill.state)}>{pill.label}</div>}
    </div>
  );
}

function CarrierBreakdown({ rows, grandTotals }) {
  function handleExport() {
    if (!rows.length) return;
    try {
      exportCarrierRowsToXlsx(
        rows,
        grandTotals,
        `conciliacao-frete-por-transportadora-${timestampSlug()}.xlsx`
      );
    } catch (err) {
      console.error("Shipping — erro export xlsx:", err);
      window.alert("Erro ao exportar XLSX. Verifique o console.");
    }
  }

  return (
    <section style={breakdown.wrap}>
      <div style={breakdown.head}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h3 style={breakdown.title}>POR TRANSPORTADORA</h3>
          <span style={breakdown.sub}>
            Pago à transportadora (planilha) vs. frete recebido do cliente (apiIDW)
          </span>
        </div>
        <button
          type="button"
          style={actions.exportBtn}
          onClick={handleExport}
          title="Baixar tabela em .xlsx"
        >
          <SheetIcon />
          EXPORTAR XLSX
        </button>
      </div>
      <table style={breakdown.table}>
        <thead>
          <tr>
            <th style={breakdown.th}>Transportadora</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Pedidos</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Pago à transp.</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Frete cliente</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Saldo</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>% frete cliente</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Peso total</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>R$ / kg</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Não encontradas</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const saldo = r.totalShippingCustomer - r.totalPaidCarrier;
            const state = balanceState(saldo);
            const pricePerKg = r.totalKg > 0 ? r.totalPaidCarrier / r.totalKg : null;
            return (
              <tr key={r.id}>
                <td style={breakdown.td}>
                  <span style={breakdown.carrierTag(r.idx)}>
                    <span style={breakdown.swatch(colors.carriers[r.idx % colors.carriers.length])} />
                    {r.label}
                  </span>
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {fmtNumber(r.matched)}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {fmtMoney(r.totalPaidCarrier)}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {fmtMoney(r.totalShippingCustomer)}
                </td>
                <td
                  style={{
                    ...breakdown.td,
                    ...breakdown.tdRight,
                    ...(state === "good" ? breakdown.tdGood : state === "bad" ? breakdown.tdBad : {}),
                  }}
                >
                  {fmtMoney(saldo)}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {r.totalPaidCarrier > 0
                    ? fmtPct((r.totalShippingCustomer / r.totalPaidCarrier) * 100)
                    : "—"}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {fmtKg(r.totalKg)}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {pricePerKg != null ? fmtMoney(pricePerKg) : "—"}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {fmtNumber(r.missing)}
                </td>
              </tr>
            );
          })}
          <tr style={breakdown.totalRow}>
            <td style={breakdown.td}>TOTAL</td>
            <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
              {fmtNumber(grandTotals.matched)}
            </td>
            <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
              {fmtMoney(grandTotals.totalPaidCarrier)}
            </td>
            <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
              {fmtMoney(grandTotals.totalShippingCustomer)}
            </td>
            <td
              style={{
                ...breakdown.td,
                ...breakdown.tdRight,
                ...(balanceState(grandTotals.saldo) === "good"
                  ? breakdown.tdGood
                  : balanceState(grandTotals.saldo) === "bad"
                  ? breakdown.tdBad
                  : {}),
              }}
            >
              {fmtMoney(grandTotals.saldo)}
            </td>
            <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
              {grandTotals.totalPaidCarrier > 0
                ? fmtPct(
                    (grandTotals.totalShippingCustomer / grandTotals.totalPaidCarrier) *
                      100
                  )
                : "—"}
            </td>
            <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
              {fmtKg(grandTotals.totalKg)}
            </td>
            <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
              {grandTotals.totalKg > 0
                ? fmtMoney(grandTotals.totalPaidCarrier / grandTotals.totalKg)
                : "—"}
            </td>
            <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
              {fmtNumber(grandTotals.missing)}
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

function ChannelBreakdown({ rows }) {
  if (!rows.length) return null;
  return (
    <section style={breakdown.wrap}>
      <div style={breakdown.head}>
        <h3 style={breakdown.title}>POR CANAL DE VENDA</h3>
        <span style={breakdown.sub}>
          Apenas pedidos retornados pela apiIDW
        </span>
      </div>
      <table style={breakdown.table}>
        <thead>
          <tr>
            <th style={breakdown.th}>Canal</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Pedidos</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Valor produto</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Frete cliente</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Pago à transp.</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Saldo frete</th>
            <th style={{ ...breakdown.th, ...breakdown.thRight }}>Frete / venda</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const saldo = r.totalShippingCustomer - r.totalPaidCarrier;
            const state = balanceState(saldo);
            const total = r.totalProduct + r.totalShippingCustomer;
            return (
              <tr key={r.channel}>
                <td style={breakdown.td}>{r.channel}</td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {fmtNumber(r.count)}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {fmtMoney(r.totalProduct)}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {fmtMoney(r.totalShippingCustomer)}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {fmtMoney(r.totalPaidCarrier)}
                </td>
                <td
                  style={{
                    ...breakdown.td,
                    ...breakdown.tdRight,
                    ...(state === "good" ? breakdown.tdGood : state === "bad" ? breakdown.tdBad : {}),
                  }}
                >
                  {fmtMoney(saldo)}
                </td>
                <td style={{ ...breakdown.td, ...breakdown.tdRight }}>
                  {total > 0 ? fmtPct((r.totalShippingCustomer / total) * 100) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Gráficos pizza
// ─────────────────────────────────────────────────────────────────────────────
const CHANNEL_PALETTE = [
  "#D04638",
  "#E08A1E",
  "#2A75B0",
  "#2C8D5E",
  "#7A4FB1",
  "#B83B6B",
  "#3F8DA1",
  "#A87320",
  "#5C5C5C",
];

function PieTooltip({ active, payload, valueFormatter }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div style={charts.tooltip}>
      <div style={charts.tooltipLabel}>{p.name}</div>
      <div style={charts.tooltipValue}>{valueFormatter(p.value)}</div>
      {p.payload?._pct != null && (
        <div style={charts.tooltipPct}>{fmtPct(p.payload._pct)}</div>
      )}
    </div>
  );
}

function PieBlock({ title, sub, data, palette, valueFormatter, idx = 0 }) {
  const total = data.reduce((s, x) => s + x.value, 0);
  const enriched = data.map((d) => ({
    ...d,
    _pct: total > 0 ? (d.value / total) * 100 : 0,
  }));

  if (!enriched.length || total === 0) {
    return (
      <div style={charts.card(idx)}>
        <div style={charts.cardHead}>
          <h4 style={charts.cardTitle}>{title}</h4>
          {sub && <span style={charts.cardSub}>{sub}</span>}
        </div>
        <div style={charts.empty}>Sem dados</div>
      </div>
    );
  }

  return (
    <div style={charts.card(idx)}>
      <div style={charts.cardHead}>
        <h4 style={charts.cardTitle}>{title}</h4>
        {sub && <span style={charts.cardSub}>{sub}</span>}
      </div>
      <div style={charts.pieWrap}>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={enriched}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={58}
              outerRadius={92}
              paddingAngle={2}
              stroke={colors.surface}
              strokeWidth={2}
            >
              {enriched.map((d, i) => (
                <Cell key={i} fill={d.fill || palette[i % palette.length]} />
              ))}
            </Pie>
            <Tooltip
              content={<PieTooltip valueFormatter={valueFormatter} />}
            />
            <Legend
              verticalAlign="bottom"
              align="center"
              iconType="square"
              wrapperStyle={charts.legend}
              formatter={(value) => (
                <span style={charts.legendItem}>{value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={charts.totalRow}>
        <span style={charts.totalLabel}>TOTAL</span>
        <span style={charts.totalValue}>{valueFormatter(total)}</span>
      </div>
    </div>
  );
}

function ChartsPanel({ carrierRows, channelRows }) {
  const paidByCarrier = carrierRows.map((r) => ({
    name: r.label,
    value: r.totalPaidCarrier,
    fill: colors.carriers[r.idx % colors.carriers.length],
  }));
  const shippingByCarrier = carrierRows.map((r) => ({
    name: r.label,
    value: r.totalShippingCustomer,
    fill: colors.carriers[r.idx % colors.carriers.length],
  }));
  const ordersByCarrier = carrierRows.map((r) => ({
    name: r.label,
    value: r.matched,
    fill: colors.carriers[r.idx % colors.carriers.length],
  }));
  const paidByChannel = channelRows.map((r) => ({
    name: r.channel,
    value: r.totalPaidCarrier,
  }));
  const ordersByChannel = channelRows.map((r) => ({
    name: r.channel,
    value: r.count,
  }));

  return (
    <section style={charts.wrap}>
      <div style={charts.head}>
        <h3 style={charts.title}>DISTRIBUIÇÕES</h3>
        <span style={charts.sub}>Visão proporcional dos custos e volumes</span>
      </div>
      <div style={charts.grid}>
        <PieBlock
          idx={0}
          title="CUSTO POR TRANSPORTADORA"
          sub="R$ pago à transportadora"
          data={paidByCarrier}
          palette={CHANNEL_PALETTE}
          valueFormatter={fmtMoney}
        />
        <PieBlock
          idx={1}
          title="FRETE CLIENTE POR TRANSPORTADORA"
          sub="R$ recebido do cliente"
          data={shippingByCarrier}
          palette={CHANNEL_PALETTE}
          valueFormatter={fmtMoney}
        />
        <PieBlock
          idx={2}
          title="PEDIDOS POR TRANSPORTADORA"
          sub="Quantidade de pedidos conciliados"
          data={ordersByCarrier}
          palette={CHANNEL_PALETTE}
          valueFormatter={fmtNumber}
        />
        <PieBlock
          idx={3}
          title="CUSTO POR CANAL DE VENDA"
          sub="R$ pago à transportadora por canal"
          data={paidByChannel}
          palette={CHANNEL_PALETTE}
          valueFormatter={fmtMoney}
        />
        <PieBlock
          idx={4}
          title="PEDIDOS POR CANAL DE VENDA"
          sub="Quantidade de pedidos por canal"
          data={ordersByChannel}
          palette={CHANNEL_PALETTE}
          valueFormatter={fmtNumber}
        />
      </div>
    </section>
  );
}

function IssuesPanel({ notFound }) {
  return (
    <section style={issues.wrap}>
      <div style={issues.head}>
        <h3 style={issues.title}>NÃO ENCONTRADAS NA API</h3>
        <span style={issues.sub}>
          {notFound.length === 0
            ? "Tudo conciliado ✓"
            : `${fmtNumber(notFound.length)} chave(s) sem correspondência`}
        </span>
      </div>
      {notFound.length === 0 ? (
        <div style={issues.empty}>
          Nenhuma chave divergente entre as planilhas e a apiIDW.
        </div>
      ) : (
        <div style={issues.list}>
          {notFound.map((item, i) => (
            <span key={i} style={issues.chip}>
              {item.carrier}: {item.key}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Página
// ─────────────────────────────────────────────────────────────────────────────
function Shipping(props) {
  const {
    location: { idPage },
  } = props;

  const [auth, setAuth] = useState(null);
  const [datasets, setDatasets] = useState({}); // { [carrierId]: { items, totalRows, totalKeys, totalValue, fileName } }
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);

  // ── Base de pedidos do mês (carrega antes de liberar uploads) ────────────
  const [baseline, setBaseline] = useState({
    loading: false,
    done: false,
    error: null,
    dateFrom: null,
    dateTo: null,
    page: 0,
    count: 0,
    totalShipping: 0,
    byNF: null,
    byOrder: null,
  });

  // ── Auth ────────────────────────────────────────────────────────────────
  const verifyAuth = useCallback(async () => {
    try {
      const { profile } = store.getState().user;
      const res = await apiLabs.post("valid_key", {
        id: profile.id,
        menu_id: idPage,
      });
      if (res.data.message === "User not authorized") return setAuth("denied");
      if (res.data.message === "User authorized") return setAuth("authorized");
      history.push({ pathname: "/", state: { from: props.location } });
    } catch (err) {
      console.warn("Shipping — erro auth:", err);
      setAuth("denied");
    }
  }, [idPage, props.location]);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  // ── Base do mês: busca todos os pedidos (início do mês → D-1) ────────────
  //   apiIDW /orders paginada: 2000 registros/página, 500ms entre chamadas,
  //   até a página voltar com menos de 2000 registros.
  const fetchBaseline = useCallback(async () => {
    const { dateFrom, dateTo } = getBaselineRange();
    setBaseline((prev) => ({
      ...prev,
      loading: true,
      done: false,
      error: null,
      dateFrom,
      dateTo,
      page: 0,
      count: 0,
      totalShipping: 0,
    }));

    try {
      const byNF = new Map();
      const byOrder = new Map();
      let totalShipping = 0;
      let count = 0;
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        const r = await apiIDW.get("/orders", {
          params: {
            DateFrom: dateFrom,
            DateTo: dateTo,
            IDOrderType: BASELINE_ORDER_TYPES,
            Page: page,
          },
        });
        const data = Array.isArray(r.data) ? r.data : [];

        for (const rec of data) {
          totalShipping += parseCurrency(rec.ValueShipping) || 0;
          if (rec.NfeNumber != null) byNF.set(String(rec.NfeNumber), rec);
          if (rec.Order) byOrder.set(String(rec.Order), rec);
        }
        count += data.length;

        setBaseline((prev) => ({ ...prev, page, count }));

        hasMore = data.length >= PAGE_SIZE;
        page += 1;
        if (hasMore) await sleep(PAGE_DELAY_MS);
      }

      setBaseline({
        loading: false,
        done: true,
        error: null,
        dateFrom,
        dateTo,
        page: page - 1,
        count,
        totalShipping,
        byNF,
        byOrder,
      });
    } catch (err) {
      console.error("Shipping — erro ao carregar base do mês:", err);
      setBaseline((prev) => ({
        ...prev,
        loading: false,
        done: false,
        error:
          "Não foi possível carregar a base de pedidos do mês. Clique para tentar novamente.",
      }));
    }
  }, []);

  // Carrega a base automaticamente após autorização
  useEffect(() => {
    if (auth === "authorized" && !baseline.done && !baseline.loading && !baseline.error) {
      fetchBaseline();
    }
  }, [auth, baseline.done, baseline.loading, baseline.error, fetchBaseline]);

  // ── Upload ──────────────────────────────────────────────────────────────
  const handlePick = useCallback(async (carrierId, file) => {
    setError(null);
    const carrier = CARRIER_BY_ID[carrierId];
    if (!carrier) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const parsed = parseSheet(wb, carrier);

      if (!parsed.items.length) {
        setError(
          `A planilha de ${carrier.label} foi lida mas não retornou nenhuma linha válida. Verifique se as colunas existem.`
        );
        return;
      }

      setDatasets((prev) => ({
        ...prev,
        [carrierId]: {
          ...parsed,
          fileName: file.name,
        },
      }));
    } catch (err) {
      console.error("Shipping — erro ao ler planilha:", err);
      setError(`Erro ao ler planilha de ${carrier.label}: ${err.message || err}`);
    }
  }, []);

  const handleClear = useCallback((carrierId) => {
    setDatasets((prev) => {
      const next = { ...prev };
      delete next[carrierId];
      return next;
    });
  }, []);

  const handleClearAll = useCallback(() => {
    if (busy) return;
    setDatasets({});
    setResult(null);
    setError(null);
  }, [busy]);

  // ── Totais prontos para consulta ────────────────────────────────────────
  const totals = useMemo(() => {
    let totalKeys = 0;
    let totalNFs = 0;
    let totalOrders = 0;
    for (const c of CARRIERS) {
      const ds = datasets[c.id];
      if (!ds) continue;
      totalKeys += ds.totalKeys;
      if (c.lookup === "nf") totalNFs += ds.totalKeys;
      else totalOrders += ds.totalKeys;
    }
    return { totalKeys, totalNFs, totalOrders };
  }, [datasets]);

  // ── Processamento (consulta apiIDW em lotes de 15) ──────────────────────
  const handleProcess = useCallback(async () => {
    if (totals.totalKeys === 0 || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);

    try {
      // Tabela de planilha por carrier+key
      // (várias transportadoras podem usar a mesma chave; mantemos lista)
      const planByCarrierKey = new Map(); // `${carrierId}::${key}` -> planilhaValue
      const nfRequests = new Map(); // nf -> Set(carrierId)
      const orderRequests = new Map(); // order -> Set(carrierId)

      for (const c of CARRIERS) {
        const ds = datasets[c.id];
        if (!ds) continue;
        for (const it of ds.items) {
          planByCarrierKey.set(`${c.id}::${it.key}`, it.value);
          const target = c.lookup === "nf" ? nfRequests : orderRequests;
          if (!target.has(it.key)) target.set(it.key, new Set());
          target.get(it.key).add(c.id);
        }
      }

      const apiByNF = new Map();
      const apiByOrder = new Map();

      // Reaproveita a base do mês já carregada — só consulta as chaves ausentes
      if (baseline.byNF) for (const [k, v] of baseline.byNF) apiByNF.set(k, v);
      if (baseline.byOrder) for (const [k, v] of baseline.byOrder) apiByOrder.set(k, v);

      const nfList = Array.from(nfRequests.keys()).filter((k) => !apiByNF.has(k));
      const orderList = Array.from(orderRequests.keys()).filter((k) => !apiByOrder.has(k));
      const nfBatches = chunk(nfList, BATCH_SIZE);
      const orderBatches = chunk(orderList, BATCH_SIZE);

      const totalBatches = nfBatches.length + orderBatches.length;
      let done = 0;
      setProgress({ current: 0, total: totalBatches });

      // Dispara em série para não estourar rate limit
      for (const batch of nfBatches) {
        try {
          const r = await apiIDW.get("/orders", {
            params: { NfeNumber: batch.join(",") },
          });
          const list = Array.isArray(r.data) ? r.data : [];
          for (const rec of list) {
            const nf = rec.NfeNumber != null ? String(rec.NfeNumber) : null;
            if (nf) apiByNF.set(nf, rec);
          }
        } catch (err) {
          console.warn("Shipping — erro batch NF:", err);
        }
        done++;
        setProgress({ current: done, total: totalBatches });
        if (BATCH_DELAY_MS > 0) await sleep(BATCH_DELAY_MS);
      }

      for (const batch of orderBatches) {
        try {
          const r = await apiIDW.get("/orders", {
            params: { Order: batch.join(",") },
          });
          const list = Array.isArray(r.data) ? r.data : [];
          for (const rec of list) {
            if (rec.Order) apiByOrder.set(String(rec.Order), rec);
          }
        } catch (err) {
          console.warn("Shipping — erro batch Order:", err);
        }
        done++;
        setProgress({ current: done, total: totalBatches });
        if (BATCH_DELAY_MS > 0) await sleep(BATCH_DELAY_MS);
      }

      // ── Agregação ────────────────────────────────────────────────────
      const byCarrier = {};
      const byChannel = {};
      const notFound = [];
      let grandTotalPaid = 0;
      let grandTotalShipping = 0;
      let grandTotalProduct = 0;
      let grandTotalKg = 0;
      let grandMatched = 0;
      let grandMissing = 0;

      const ensureCarrier = (cfg, idx) => {
        if (!byCarrier[cfg.id]) {
          byCarrier[cfg.id] = {
            id: cfg.id,
            label: cfg.label,
            idx,
            matched: 0,
            missing: 0,
            totalPaidCarrier: 0,
            totalShippingCustomer: 0,
            totalProduct: 0,
            totalKg: 0,
          };
        }
        return byCarrier[cfg.id];
      };

      const resolveChannel = (rec) => {
        const raw = rec.TypeOrder;
        if (!raw) return "Sem canal";
        return String(raw).trim() || "Sem canal";
      };

      CARRIERS.forEach((cfg, idx) => {
        const ds = datasets[cfg.id];
        if (!ds) return;
        const bucket = ensureCarrier(cfg, idx);

        for (const it of ds.items) {
          const rec = cfg.lookup === "nf" ? apiByNF.get(it.key) : apiByOrder.get(it.key);
          bucket.totalPaidCarrier += it.value;
          grandTotalPaid += it.value;

          if (!rec) {
            bucket.missing += 1;
            grandMissing += 1;
            notFound.push({ carrier: cfg.label, key: it.key });
            continue;
          }

          bucket.matched += 1;
          grandMatched += 1;

          const shipping = parseCurrency(rec.ValueShipping) || 0;
          const product = parseCurrency(rec.ValueProduct) || 0;
          const weight = parseCurrency(rec.Weight) || 0;
          bucket.totalShippingCustomer += shipping;
          bucket.totalProduct += product;
          bucket.totalKg += weight;
          grandTotalShipping += shipping;
          grandTotalProduct += product;
          grandTotalKg += weight;

          const channel = resolveChannel(rec);
          if (!byChannel[channel]) {
            byChannel[channel] = {
              channel,
              count: 0,
              totalProduct: 0,
              totalShippingCustomer: 0,
              totalPaidCarrier: 0,
            };
          }
          const cb = byChannel[channel];
          cb.count += 1;
          cb.totalProduct += product;
          cb.totalShippingCustomer += shipping;
          cb.totalPaidCarrier += it.value;
        }
      });

      const carrierRows = Object.values(byCarrier).sort(
        (a, b) => b.totalPaidCarrier - a.totalPaidCarrier
      );
      const channelRows = Object.values(byChannel).sort(
        (a, b) => b.totalPaidCarrier - a.totalPaidCarrier
      );

      const totalSales = grandTotalProduct + grandTotalShipping;
      const saldoFrete = grandTotalShipping - grandTotalPaid;

      setResult({
        carrierRows,
        channelRows,
        notFound,
        grandTotals: {
          matched: grandMatched,
          missing: grandMissing,
          totalPaidCarrier: grandTotalPaid,
          totalShippingCustomer: grandTotalShipping,
          totalProduct: grandTotalProduct,
          totalKg: grandTotalKg,
          totalSales,
          saldoFrete,
          pctFreteVsSales: totalSales > 0 ? (grandTotalShipping / totalSales) * 100 : 0,
          pctRecuperacao: grandTotalPaid > 0 ? (grandTotalShipping / grandTotalPaid) * 100 : 0,
        },
      });
    } catch (err) {
      console.error("Shipping — erro geral:", err);
      setError(
        "Erro ao processar planilhas. Verifique a conexão com a apiIDW e tente novamente."
      );
    } finally {
      setBusy(false);
      setProgress({ current: 0, total: 0 });
    }
  }, [datasets, totals.totalKeys, busy, baseline.byNF, baseline.byOrder]);

  // ── Export PNG ──────────────────────────────────────────────────────────
  const handleExportPng = useCallback(async () => {
    if (!exportRef.current || exporting) return;
    setExporting(true);
    try {
      await exportNodeToPng(
        exportRef.current,
        `conciliacao-frete-${timestampSlug()}.png`
      );
    } catch (err) {
      console.error("Shipping — erro export PNG:", err);
      setError("Não foi possível gerar o PNG. Tente novamente.");
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Conciliação de Frete">
      {auth === "denied" && <Denied />}
      {auth === "authorized" && (
        <>
          <style>{globalCSS}</style>
          <div style={page}>
            <div style={dash}>
              {/* HEADER */}
              <header style={header.wrap}>
                <div>
                  <h1 style={header.title}>CONCILIAÇÃO DE FRETE</h1>
                  <p style={header.subtitle}>
                    Suba planilhas das transportadoras · consulta lotes de {BATCH_SIZE} na apiIDW · saldo frete cliente vs. custo operacional
                  </p>
                </div>
                <div style={header.right}>
                  {Object.keys(datasets).length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAll}
                      style={header.clearBtn}
                      title="Limpar todos os uploads"
                    >
                      <TrashIcon />
                      LIMPAR UPLOADS
                    </button>
                  )}
                </div>
              </header>

              {/* ERRO */}
              {error && (
                <div
                  style={{
                    background: colors.badSoft,
                    border: `1px solid ${colors.bad}`,
                    borderRadius: 10,
                    padding: "12px 18px",
                    color: colors.bad,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  ⚠ {error}
                </div>
              )}

              {/* BASE DO MÊS */}
              <BaselinePanel baseline={baseline} onReload={fetchBaseline} />

              {/* UPLOADS — liberados só após a base do mês carregar */}
              {baseline.done ? (
                <>
                  <section style={uploads.wrap}>
                    <div style={uploads.head}>
                      <h2 style={uploads.title}>UPLOAD DE PLANILHAS</h2>
                      <span style={uploads.sub}>
                        Pode subir individualmente · uploads existentes não são perdidos
                      </span>
                    </div>
                    <div style={uploads.grid}>
                      {CARRIERS.map((c, i) => (
                        <UploadCard
                          key={c.id}
                          idx={i}
                          carrier={c}
                          dataset={datasets[c.id]}
                          onPick={handlePick}
                          onClear={handleClear}
                        />
                      ))}
                    </div>
                  </section>

                  {/* AÇÕES */}
                  <ActionsBar
                    totals={totals}
                    busy={busy}
                    progress={progress}
                    onProcess={handleProcess}
                    hasResult={Boolean(result)}
                    onClearResult={() => setResult(null)}
                    onExportPng={handleExportPng}
                    exporting={exporting}
                  />
                </>
              ) : (
                <section style={uploads.locked}>
                  {baseline.loading
                    ? "Carregando a base de pedidos do mês… o upload de planilhas será liberado ao concluir."
                    : "Carregue a base de pedidos do mês para liberar o upload das planilhas."}
                </section>
              )}

              {/* RESULTADOS */}
              {result && (
                <div ref={exportRef} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                  <section style={kpis.grid}>
                    <Kpi
                      idx={0}
                      label="TOTAL PAGO À TRANSPORTADORA"
                      value={fmtMoney(result.grandTotals.totalPaidCarrier)}
                      sub={`${fmtNumber(result.grandTotals.matched + result.grandTotals.missing)} chaves processadas`}
                    />
                    <Kpi
                      idx={1}
                      label="TOTAL FRETE PAGO PELO CLIENTE"
                      value={fmtMoney(result.grandTotals.totalShippingCustomer)}
                      sub={`${fmtNumber(result.grandTotals.matched)} pedidos conciliados`}
                    />
                    <Kpi
                      idx={2}
                      label="SALDO (CLIENTE − TRANSPORTADORA)"
                      value={fmtMoney(result.grandTotals.saldoFrete)}
                      pill={{
                        state: balanceState(result.grandTotals.saldoFrete),
                        label:
                          result.grandTotals.saldoFrete > 0
                            ? "POSITIVO"
                            : result.grandTotals.saldoFrete < 0
                            ? "NEGATIVO"
                            : "NEUTRO",
                      }}
                      sub={`Recuperação ${fmtPct(result.grandTotals.pctRecuperacao)} do custo`}
                    />
                    <Kpi
                      idx={3}
                      label="% FRETE CLIENTE / TOTAL DA COMPRA"
                      value={fmtPct(result.grandTotals.pctFreteVsSales, 2)}
                      sub={`Valor mercadoria: ${fmtMoney(result.grandTotals.totalProduct)} · Compra total: ${fmtMoney(result.grandTotals.totalSales)}`}
                    />
                  </section>

                  <CarrierBreakdown
                    rows={result.carrierRows}
                    grandTotals={{
                      matched: result.grandTotals.matched,
                      missing: result.grandTotals.missing,
                      totalPaidCarrier: result.grandTotals.totalPaidCarrier,
                      totalShippingCustomer: result.grandTotals.totalShippingCustomer,
                      totalKg: result.grandTotals.totalKg,
                      saldo: result.grandTotals.saldoFrete,
                    }}
                  />

                  <ChartsPanel
                    carrierRows={result.carrierRows}
                    channelRows={result.channelRows}
                  />

                  <ChannelBreakdown rows={result.channelRows} />

                  <IssuesPanel notFound={result.notFound} />
                </div>
              )}

              {/* FOOTER */}
              <footer style={footer.wrap}>
                <div style={footer.legend}>
                  {CARRIERS.map((c, i) => (
                    <div key={c.id} style={footer.item}>
                      <span style={footer.swatch(colors.carriers[i % colors.carriers.length])} />
                      {c.label} · {c.lookup === "nf" ? "por NF" : "por Pedido"}
                    </div>
                  ))}
                </div>
                <div>Lotes de {BATCH_SIZE} chaves por requisição</div>
              </footer>
            </div>
          </div>
        </>
      )}
    </Dashboard>
  );
}

export default Shipping;
